import fetch from '@mailobj-browser/front/js/fetchers/fetch.js'
import html from '@mailobj-browser/front/js/fetchers/html.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import defaults from '@mailobj-browser/front/js/selectors/defaults.js'
import meta from '@mailobj-browser/front/js/selectors/meta.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import credentials from '#utilities/js/ea/credentials.js'
import globals from '#utilities/js/ea/globals.js'
import { modifiers } from '../styles.js'
import { lock, unlock } from './cooldown.js'
import href from './href.js'
import { shim } from './submitter.js'
import upload from './upload.js'

export const formAction = (form, submitter) => {
  return submitter?.hasAttribute?.('formaction')
    ? submitter.formAction
    : form.action
}

const formMethod = (form, submitter) => {
  return submitter?.hasAttribute?.('formmethod')
    ? submitter.formMethod
    : form.method ?? 'GET'
}

const anchor = (
  target,
  event,
  params
) => {
  const { download, href: url } = target

  return get(href(url, params), {
    ...!download && { target }
  })
}

const form = (
  target,
  event
) => {
  const { ownerDocument } = target
  const { submitter } = shim(event)
  const { name, value } = submitter ?? {}
  const action = formAction(target, submitter)
  const method = formMethod(target, submitter)
  const { defaultView } = ownerDocument
  const { FormData, URL, URLSearchParams } = defaultView
  const body = new FormData(target)

  if (name) {
    body.append(name, value)
  }

  if (method.toUpperCase() === 'GET') {
    const url = new URL(action)
    const { searchParams } = url

    url.search = new URLSearchParams(new Map([...searchParams, ...body]))

    return get(`${url}`, { target })
  }

  const { dataset } = target
  const { sseEvent } = dataset

  if (sseEvent === 'progress') {
    try {
      const { location } = ownerDocument
      const [route] = meta('sse', ownerDocument)
      const url = new URL(route, location)
      const files = all('input[type="file"]', target)

      upload(target, body, `${url}`, files)
    } catch (error) {
      console.warn('Missing meta[name="ea_sse"][content]')
    }
  }

  return post(action, body, { submitter, target })
}

const fetcher = (target, submitter = null) => {
  if (submitter || target.matches?.('a')) {
    return submitter ?? target
  }

  if (target.matches?.('template[data-url]')) {
    const { parentNode } = target

    return parentNode
  }
}

const call = async (
  request,
  { enqueue, submitter = null, target = null } = {}
) => {
  if (target) {
    const { ownerDocument } = target
    const fetching = fetcher(target, submitter)

    lock(ownerDocument)
    fetching?.classList.add(modifiers.any.fetching)

    const { response } = await html(object(null, {
      enqueue,
      request,
      target
    }))

    fetching?.classList.remove(modifiers.any.fetching)
    unlock(ownerDocument)

    return response
  }

  return fetch(object(null, { enqueue, request }))
}

export const get = async (
  url,
  { enqueue = true, target = null } = {}
) => {
  const { window } = defaults
  const { Request } = window
  const method = 'GET'
  const options = { ...credentials(url), method }
  const request = new Request(url, options)

  return call(request, { enqueue, target })
}

export const post = async (
  url,
  body,
  { enqueue = true, target = null } = {}
) => {
  const { window } = defaults
  const { Request } = window
  const method = 'POST'
  const options = { body, ...credentials(url), method }
  const request = new Request(url, options)

  return call(request, { enqueue, target })
}

export const fromEvent = async (
  target,
  event
) => {
  const { type } = event
  const request = type === 'submit' ? form : anchor
  const result = await request(target, event)

  if (typeof globals.ea_screen_adjust === 'function') { // Pour les pages externes (register)
    globals.ea_screen_adjust() // A supprimer quand ancienne structure sera supprimée
  }

  return result
}
